/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  StyledAvatar,
  StyledTooltipContainer,
  StyledTooltipDetails,
  StyledTooltipEmail,
  StyledTooltipName,
} from '@components/lib/avatar-tooltip/avatar-tooltip.styles';
import {
  faInfoCircle,
  faMagnifyingGlass,
  faSend,
} from '@fortawesome/pro-regular-svg-icons';
import { faCaretDown } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FollowUpKeys } from '@gen2/api/follow-up/hooks';
import { InviteRequestsKeys } from '@gen2/api/invite-requests/hooks';
import {
  InvitesKeys,
  useRemoveContactFromInviteMutation,
  useUpdateInviteAssigneeMutation,
  useUpdateInviteTeamMutation,
} from '@gen2/api/invites/hooks';
import { Keys } from '@gen2/api/team-member/hooks';
import { useActionModalStore } from '@gen2/app/components/action-modal/store';
import { StringAvatarBox } from '@gen2/app/components/avatar/avatar.styled';
import LoadTemplate from '@gen2/app/components/load-template/load-template';
import Watchers from '@gen2/app/components/watchers/watchers';
import {
  StyledAssignee,
  StyledLabel,
} from '@gen2/app/invite-listing/assignee/assignee.styled';
import {
  StyledTeam,
  StyledLabel as StyledTeamLabel,
} from '@gen2/app/invite-listing/team/team.styled';
import { queryClient } from '@gen2/config';
import { useAuth, useToast } from '@gen2/hooks';
import { PermissionsKeys } from '@gen2/types/permissions';
import { TTeam } from '@gen2/types/team';
import { TBasicUser } from '@gen2/types/user';
import { contactsToTags } from '@gen2/utils/contacts';
import { stringAvatar, stringInitialsAvatar } from '@gen2/utils/name';
import { usePermissions } from '@gen2/utils/permissions/permissions';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  Autocomplete,
  Button,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { AxiosResponse } from 'axios';
import dayjs, { Dayjs } from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { get } from 'lodash';
import { SyntheticEvent, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { useSendInvite } from './hooks';
import { MAX_CONTACT_LIMIT, MAX_SUBJECT_LENGTH } from './schema';
import { TDraftInviteForm } from './send-invites';
import {
  AlertContainer,
  Stickyhead,
  StyledActions,
  StyledAlert,
  StyledAlertButton,
  StyledPointOfContact,
  StyledReminder,
  StyledSendTo,
  Styledspan,
  StyledSubjectLine,
  StyledLoadTemplateBox,
  StyleStack,
  StyledDueDate,
  FlexBox,
  StyledHead,
  RightContent,
  DateFrame,
  SettingsFrame,
} from './send-invites.styled';
import { useSendInviteStore } from './store';
import { useAssignee } from './useAssignee';
import { usePointOfContactAutocomplete } from './usePointOfContactAutocomplete';
import { useTeam } from './useTeam';
dayjs.extend(utc);
dayjs.extend(timezone);

type TInviteHeadProps = {
  almostDueAnchor: React.RefObject<HTMLButtonElement>;
};

export const createTooltip = (avatar: string, name: string, email: string) => {
  return (
    <StyledTooltipContainer>
      <StyledAvatar data-cy="header-avatar-btn">{avatar}</StyledAvatar>
      <StyledTooltipDetails>
        <StyledTooltipName>{name}</StyledTooltipName>
        <StyledTooltipEmail>{email}</StyledTooltipEmail>
      </StyledTooltipDetails>
    </StyledTooltipContainer>
  );
};

const InviteHead = ({ almostDueAnchor }: TInviteHeadProps) => {
  const {
    register,
    watch,
    setValue,
    clearErrors,
    formState: { errors },
  } = useFormContext<TDraftInviteForm>();
  const store = useSendInviteStore();
  const params = useParams<{ id: string }>();
  // for new invite draft & updating invite both
  const isInviteIdReady = !!store.contextInviteIdForNewInvite || params.id;
  const { t } = useTranslation('sendInvite');
  const { user } = useAuth();
  const theme = useTheme();
  const tablet = useMediaQuery(theme.breakpoints.up('sm'));
  const { mutateAsync: removeContactFromInviteMutation } =
    useRemoveContactFromInviteMutation();
  const toast = useToast();

  const {
    mixedPointofContacts,
    isPointofContactsLoading,
    selectedPointOfContact,
    setSelectedPointOfContact,
    setSearch,
    showAvatar,
    setShowAvatar,
    isSearching,
    setIsSearching,
  } = usePointOfContactAutocomplete();

  const {
    optionsAssignee,
    isUsersLoading,
    selectedAssignee,
    setSelectedAssignee,
    setSearchAssignee,
  } = useAssignee();

  const {
    optionsTeam,
    isTeamsLoading,
    selectedTeam,
    setSelectedTeam,
    setSearchTeam,
  } = useTeam();

  const { onSave, onSend, isSending } = useSendInvite();
  const { mutate: updateTeamMutation } = useUpdateInviteTeamMutation();
  const { mutate: updateAssigneeMutation } = useUpdateInviteAssigneeMutation();

  const { showModal } = useActionModalStore();
  const { withPermissions } = usePermissions();

  // Check assignee permissions
  const hasShowAssigneePermission = withPermissions(
    PermissionsKeys.SHOW_INVITE_ASSIGNEE,
  );
  const hasUpdateAssigneePermission = withPermissions(
    PermissionsKeys.UPDATE_INVITE_ASSIGNEE,
  );

  // Check team permissions
  const hasShowTeamPermission = withPermissions(
    PermissionsKeys.SHOW_INVITE_TEAM,
  );
  const hasUpdateTeamPermission = withPermissions(
    PermissionsKeys.UPDATE_INVITE_TEAM,
  );

  // Check watcher permissions
  const hasListWatcherPermission = withPermissions(
    PermissionsKeys.LIST_WATCHER,
  );

  // Check if user has any right-side permissions (assignee, team, or watchers)
  const hasAnyRightSidePermission =
    hasShowAssigneePermission ||
    hasUpdateAssigneePermission ||
    hasShowTeamPermission ||
    hasUpdateTeamPermission ||
    hasListWatcherPermission;

  // registers
  const rSubject = register('subject');
  const rDueDate = register('due_at');

  // watches
  const wSubject = watch('subject');
  const wDueDate = watch('due_at');
  const wContacts = watch('contacts');
  const wAlmostDue = watch('almost_due_reminder');
  const wCustomAlmostDue = watch('customAlmostDueValue');

  // https://fileinvite.atlassian.net/browse/FG2-3534
  // Subject field should have empty value and only has hint text which is “Enter a subject line”
  useEffect(() => {
    if (wSubject === 'Sample Subject') {
      setValue('subject', '');
    }
  }, [setValue, wSubject]);

  // timezone setting
  const orgTimezone = get(user, 'organisations[0].timezone');
  const minDate = dayjs().tz(orgTimezone).startOf('day');
  const maxDate = dayjs()
    .tz(orgTimezone)
    .add(3, 'months')
    .endOf('day')
    .subtract(1, 'day');

  const onSendToDelete = async (id: string) => {
    if (!wContacts) return;

    store.set({ isSendFromLoading: true });

    const deleteContact = async () => {
      try {
        await removeContactFromInviteMutation({
          inviteId: store.invite.id,
          contactId: id,
        });

        setValue(
          'contacts',
          wContacts.filter((contact) => contact.id !== id),
        );

        await queryClient.invalidateQueries({
          queryKey: [InvitesKeys.getInvite],
        });
        await queryClient.invalidateQueries({
          queryKey: [InviteRequestsKeys.getInviteRequests],
        });
        await queryClient.invalidateQueries({
          queryKey: [InviteRequestsKeys.getInviteRequestGroups],
        });
        await queryClient.invalidateQueries({
          queryKey: [FollowUpKeys.getFollowUps],
        });

        const message =
          store.invite.status === 'draft'
            ? t('editSendInvite.removeContactFromDraftInvite.success')
            : t('editSendInvite.removeContactFromEditInvite.success');

        store.setBannerMessage({
          severity: 'info',
          message: message,
        });
      } catch (err) {
        const error = err as AxiosResponse;

        if (error.status === 422) {
          showModal({
            header: t('editSendInvite.removeContactFromEditInvite.error.title'),
            translationNamespace: 'sendInvite',
            message: 'editSendInvite.removeContactFromEditInvite.error.message',
            closeButtonLabel:
              t('editSendInvite.requestContact.notAssigned.close', {
                ns: 'sendInvite',
              }) ?? '',
          });
        } else {
          store.setBannerMessage({
            severity: 'error',
            message: 'Error removing contact',
          });

          console.error('Error removing contact:', error);
        }
      } finally {
        store.set({ isSendFromLoading: false });
      }
    };

    if (store.isEditSendInvite) {
      const contactToDelete = wContacts.find((contact) => contact.id === id);

      if (wContacts.length === 1) {
        const { isConfirmed } = await showModal({
          header: t('editSendInvite.deleteContact.atLeastOneContact.title'),
          message: 'editSendInvite.deleteContact.atLeastOneContact.message',
          closeButtonLabel: t('editSendInvite.deleteContact.close') ?? '',
        });
        if (!isConfirmed) {
          store.set({ isSendFromLoading: false });
        }
      } else {
        const { isConfirmed } = await showModal({
          header: t('editSendInvite.deleteContact.delete.title') ?? '',
          message: 'editSendInvite.deleteContact.delete.message',
          messageParams: {
            firstname: `${contactToDelete?.first_name}`,
            lastname: `${contactToDelete?.last_name}`,
          },
          closeButtonLabel: t('editSendInvite.deleteContact.cancel') ?? '',
          submitButtonLabel: t('editSendInvite.deleteContact.confirm') ?? '',
        });

        if (isConfirmed) {
          await deleteContact();
        } else {
          store.set({ isSendFromLoading: false });
        }
      }
    } else {
      if (!store.invite.id) {
        setValue(
          'contacts',
          wContacts.filter((contact) => contact.id !== id),
        );
        store.set({ isSendFromLoading: false });
      }
      await deleteContact();
    }
  };

  const onSendToClick = (event: React.MouseEvent<HTMLElement>) => {
    //clear contacts error status
    clearErrors('contacts');

    if ((wContacts?.length || 0) >= MAX_CONTACT_LIMIT) {
      store.setUpgradeModal({
        isOpen: true,
        title: t('contacts.upgrade.title') ?? '',
        description: t('contacts.upgrade.description') ?? '',
      });

      return;
    }

    store.setContactPopupAnchorEl(event.currentTarget);
    store.setIsContactListOpen(true);
  };

  const onSubjectChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value.length > 100) {
      return;
    }

    setValue('subject', event.target.value);

    clearErrors('subject');
    rSubject.onChange(event);
  };

  const onDueDateChange = (date: Dayjs | null) => {
    // toggle due date
    if (date && wDueDate) {
      if (date.isSame(wDueDate, 'day')) {
        setValue('due_at', wDueDate);
        onSave();
        return;
      }
    }

    // if due date is greater than max almost due then default it to "never" or reset if custom
    if (date) {
      const newMaxAlmostDue = date.day() - dayjs().day();

      if (
        newMaxAlmostDue <= Number(wAlmostDue) ||
        newMaxAlmostDue <= Number(wCustomAlmostDue)
      ) {
        setValue('almost_due_reminder', '0');
        setValue('customAlmostDueValue', '');
      }
    }

    setValue('due_at', date);
    onSave();
  };

  const openReminder = () => {
    store.setIsReminderOpen(true);
  };

  const handleFinishEdit = () => {
    toast.show({
      text: t('editSendInvite.finishEdit'),
      variant: 'success',
    });
  };

  const handleChange = async (
    event: SyntheticEvent<Element, Event>,
    newValue: TBasicUser | null,
    reason: string,
  ) => {
    if (!newValue || reason !== 'selectOption') return;

    const updatePointOfContact = async () => {
      setSelectedPointOfContact([newValue]);
      setShowAvatar(true);
      clearErrors('from_user_id');
      setValue('from_user_id', newValue.id);
      onSave();
    };

    if (store.isEditSendInvite) {
      const { isConfirmed } = await showModal({
        header: t('editSendInvite.updatePointofContact.title'),
        translationNamespace: 'sendInvite',
        message: 'editSendInvite.updatePointofContact.message',
        closeButtonLabel: t('editSendInvite.updatePointofContact.cancel') ?? '',
        submitButtonLabel:
          t('editSendInvite.updatePointofContact.confirm') ?? '',
        submitButtonColor: 'secondary',
      });
      if (isConfirmed) {
        await updatePointOfContact();
        // refresh the user list in case the selected point of contact is demoted
        await queryClient.invalidateQueries({
          queryKey: [Keys.queryTeamMemberList],
        });
      }
    } else {
      await updatePointOfContact();
      // refresh the user list in case the selected point of contact is demoted
      await queryClient.invalidateQueries({
        queryKey: [Keys.queryTeamMemberList],
      });
    }
  };

  const handleSearchInputChange = (
    event: React.ChangeEvent<object>,
    newInputValue: string,
    reason: string,
  ) => {
    clearErrors('from_user_id');
    setShowAvatar(false);
    if (reason === 'input') {
      setSearch(newInputValue);
      setIsSearching(true);
    } else if (reason === 'clear') {
      setSearch('');
      setSelectedPointOfContact([]);
      setValue('from_user_id', '');
      onSave();
    } else if (reason === 'reset') {
      setShowAvatar(true);
    }
  };

  const handleChangeAssignee = async (
    event: SyntheticEvent<Element, Event>,
    newValue: TBasicUser | null,
    reason: string,
  ) => {
    if (reason !== 'selectOption' && reason !== 'clear') return;

    const updateAssignee = async (assignee_user_id: string | null) => {
      // If invite is already created, use the dedicated assignee endpoint
      if (store.invite.id) {
        updateAssigneeMutation(
          {
            inviteId: store.invite.id,
            assignee_user_id: assignee_user_id,
          },
          {
            onSuccess: () => {
              queryClient.invalidateQueries({
                queryKey: [InvitesKeys.getInvite],
              });
            },
          },
        );
      } else {
        // For new invites (not yet saved), just update the form
        setValue('assignee_user_id', assignee_user_id ?? '');
        onSave();
      }
    };

    if (newValue) {
      setSelectedAssignee([newValue]);
      clearErrors('assignee_user_id');
      setValue('assignee_user_id', newValue.id);
      await updateAssignee(newValue.id);
    } else {
      setSelectedAssignee([]);
      clearErrors('assignee_user_id');
      await updateAssignee(null);
    }
  };

  const handleSearchInputChangeAssignee = (
    event: React.ChangeEvent<object>,
    newInputValue: string,
    reason: string,
  ) => {
    if (reason === 'input') {
      setSearchAssignee(newInputValue);
    } else if (reason === 'clear') {
      setSearchAssignee('');
      setSelectedAssignee([]);
      setValue('assignee_user_id', '');

      // If invite is already created, use the dedicated assignee endpoint to clear it
      if (store.invite.id) {
        updateAssigneeMutation(
          {
            inviteId: store.invite.id,
            assignee_user_id: null,
          },
          {
            onSuccess: () => {
              queryClient.invalidateQueries({
                queryKey: [InvitesKeys.getInvite],
              });
            },
          },
        );
      } else {
        onSave();
      }
    }
  };

  const handleChangeTeam = async (
    event: SyntheticEvent<Element, Event>,
    newValue: TTeam | null,
    reason: string,
  ) => {
    if (reason !== 'selectOption' && reason !== 'clear') return;

    const updateTeam = async (team_id: string | null) => {
      // If invite is already created, use the dedicated team endpoint
      if (store.invite.id) {
        updateTeamMutation(
          {
            inviteId: store.invite.id,
            team_id: team_id,
          },
          {
            onSuccess: () => {
              queryClient.invalidateQueries({
                queryKey: [InvitesKeys.getInvite],
              });
            },
          },
        );
      } else {
        // For new invites (not yet saved), just update the form
        setValue('team_id', team_id ?? '');
        onSave();
      }
    };

    if (newValue) {
      setSelectedTeam(newValue);
      clearErrors('team_id');
      setValue('team_id', newValue.id);
      await updateTeam(newValue.id);
    } else {
      setSelectedTeam(null);
      clearErrors('team_id');
      await updateTeam(null);
    }
  };

  const handleSearchInputChangeTeam = (
    event: React.ChangeEvent<object>,
    newInputValue: string,
    reason: string,
  ) => {
    if (reason === 'input') {
      setSearchTeam(newInputValue);
    }
  };

  // ui optiomization for text due to Debouncing Delay
  // shows noOptionsText before the search results are available
  useEffect(() => {
    if (!isPointofContactsLoading) {
      setIsSearching(false);
    }
  }, [isPointofContactsLoading, setIsSearching]);

  return (
    <div>
      <Stickyhead
        data-cy="stick-head"
        $isEditSendInvite={store.isEditSendInvite}
      >
        <StyledActions $opaque={!isInviteIdReady}>
          <StyleStack>
            {!store.isEditSendInvite && (
              <StyledLoadTemplateBox>
                <LoadTemplate
                  handleClick={() => store.setIsTemplatesOpen(true)}
                />
              </StyledLoadTemplateBox>
            )}
            {store.isEditSendInvite ? (
              <AlertContainer>
                <StyledAlert icon={false} color="warning" severity="warning">
                  {t('editSendInvite.editBanner.alert')}
                </StyledAlert>
                <Link to={`/invite-listing/${store.invite.id}`}>
                  <StyledAlertButton
                    color="primary"
                    variant="contained"
                    size="small"
                    onClick={handleFinishEdit}
                  >
                    {t('editSendInvite.editBanner.button')}
                  </StyledAlertButton>
                </Link>
              </AlertContainer>
            ) : (
              <Stack direction="row" spacing={1}>
                <LoadingButton
                  variant="contained"
                  color="primary"
                  data-cy="si-send-btn"
                  onClick={onSend}
                  loading={isSending}
                >
                  {tablet ? 'Send Invite' : <FontAwesomeIcon icon={faSend} />}
                </LoadingButton>
              </Stack>
            )}
          </StyleStack>
        </StyledActions>
      </Stickyhead>
      <StyledHead data-cy="invite-head" $opaque={!isInviteIdReady}>
        <FlexBox
          data-cy="invite-head-flex-box"
          $expandFullWidth={!hasAnyRightSidePermission}
        >
          <StyledSendTo
            label="Send to"
            data-cy="si-send-to-label"
            onDeleteTag={onSendToDelete}
            onLabelClick={onSendToClick}
            tags={contactsToTags(wContacts)}
            error={errors.contacts?.message && t(errors.contacts?.message)}
            isLoading={store.isSendFromLoading}
            disabled={store.isSendFromLoading}
            hiddenButton={false}
          />

          <StyledSubjectLine
            name={rSubject.name}
            value={wSubject}
            data-cy="si-subject-line"
            placeholder="Enter a subject line"
            label="Subject"
            error={errors.subject?.message && t(errors.subject?.message)}
            inputProps={{
              ...rSubject,
              onChange: onSubjectChange,
              onBlur: onSave,
            }}
            maxLength={MAX_SUBJECT_LENGTH}
            required
          />
          <SettingsFrame>
            <DateFrame>
              <StyledDueDate
                name={rDueDate.name}
                value={wDueDate}
                label="Due Date"
                disablePast
                minDate={minDate}
                maxDate={maxDate}
                error={errors.due_at?.message && t(errors.due_at?.message)}
                clearErrors={clearErrors}
                onChange={onDueDateChange}
                timezone={orgTimezone}
                tooltipTitle={t('due_date.tooltip')}
              />
              <StyledReminder>
                <span>{t('reminder.label')}</span>
                <Button
                  size="small"
                  variant="outlined"
                  color="tertiary"
                  ref={almostDueAnchor}
                  data-cy="si-reminder-btn"
                  onClick={openReminder}
                  endIcon={<FontAwesomeIcon icon={faCaretDown} />}
                >
                  {t('reminder.viewSettings')}
                </Button>
              </StyledReminder>
            </DateFrame>
            <StyledPointOfContact
              data-cy="si-point-of-contact"
              label={t('pointOfContact.label')}
              tooltipTitle={t('pointOfContact.tooltip') ?? ''}
              error={errors.from_user_id?.message}
              dropDownContent={
                <Autocomplete
                  id="point-of-contact-list"
                  loading={isPointofContactsLoading}
                  options={mixedPointofContacts ?? null}
                  sx={{ width: 316, lg: { width: 450 } }}
                  getOptionLabel={(option: TBasicUser) =>
                    `${option.first_name} ${option.last_name}`
                  }
                  noOptionsText={
                    isPointofContactsLoading || isSearching
                      ? t('pointOfContact.searchingPlaceholder')
                      : t('pointOfContact.noOptionsText')
                  }
                  popupIcon={<FontAwesomeIcon icon={faMagnifyingGlass} />}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  renderOption={(props: any, option: TBasicUser) => {
                    const { key, ...optionProps } = props;
                    return (
                      <div key={key} {...optionProps}>
                        <StringAvatarBox src={option.avatar_url} sx={{ mr: 1 }}>
                          {!option.avatar_url
                            ? stringAvatar(option.first_name, option.last_name)
                            : null}
                        </StringAvatarBox>
                        <Styledspan>
                          {option.first_name} {option.last_name}{' '}
                          {option?.isMyself ? `(Myself)` : null}
                        </Styledspan>
                      </div>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      placeholder={
                        t('pointOfContact.searchingPlaceholder') ?? ''
                      }
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            {showAvatar ? (
                              <StringAvatarBox
                                sx={{ mr: 1 }}
                                src={
                                  mixedPointofContacts.find(
                                    (opt) => opt.id === params.inputProps.value,
                                  )?.avatar_url
                                }
                              >
                                {!mixedPointofContacts.find(
                                  (opt) => opt.id === params.inputProps.value,
                                )?.avatar_url
                                  ? stringInitialsAvatar(
                                      params.inputProps.value as string,
                                    )
                                  : null}
                              </StringAvatarBox>
                            ) : null}
                          </InputAdornment>
                        ),
                      }}
                      error={!!errors.from_user_id?.message}
                      helperText={
                        errors.from_user_id?.message &&
                        t(errors.from_user_id?.message)
                      }
                    />
                  )}
                  value={selectedPointOfContact[0] ?? null}
                  onChange={handleChange}
                  onInputChange={handleSearchInputChange}
                />
              }
            />
          </SettingsFrame>
        </FlexBox>
        {hasAnyRightSidePermission && (
          <RightContent data-cy="right-content">
            {/* Only show assignee component if user has either show or update permission */}
            {(hasShowAssigneePermission || hasUpdateAssigneePermission) && (
              <StyledAssignee data-cy="si-assignee">
                <StyledLabel>
                  Assignee:
                  <Tooltip title={t('assignee.tooltip')} placement="top">
                    <FontAwesomeIcon icon={faInfoCircle} />
                  </Tooltip>
                </StyledLabel>
                <Autocomplete
                  id="assignee-list"
                  options={optionsAssignee}
                  sx={{ width: 300, lg: { width: 450 } }}
                  getOptionLabel={(option: TBasicUser) =>
                    `${option.first_name} ${option.last_name}`
                  }
                  noOptionsText={
                    isUsersLoading
                      ? t('assignee.loading')
                      : t('assignee.noOptions')
                  }
                  popupIcon={<FontAwesomeIcon icon={faMagnifyingGlass} />}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  disabled={!hasUpdateAssigneePermission}
                  renderOption={(props: any, option: TBasicUser) => {
                    const { key, ...optionProps } = props;

                    return (
                      <div key={key} {...optionProps}>
                        <StringAvatarBox src={option.avatar_url} sx={{ mr: 1 }}>
                          {!option.avatar_url
                            ? stringAvatar(option.first_name, option.last_name)
                            : null}
                        </StringAvatarBox>
                        <p>
                          {option.first_name} {option.last_name}{' '}
                          {option.id === user?.id ? `(Myself)` : null}
                        </p>
                      </div>
                    );
                  }}
                  renderInput={(params) => {
                    return (
                      <TextField
                        {...params}
                        variant="outlined"
                        placeholder={t('assignee.unassigned') ?? ''}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: params.inputProps.value && (
                            <InputAdornment position="start">
                              <StringAvatarBox
                                sx={{ mr: 1 }}
                                src={selectedAssignee[0]?.avatar_url ?? ''}
                              >
                                {!selectedAssignee[0] ||
                                !selectedAssignee[0]?.avatar_url
                                  ? `${selectedAssignee[0]?.first_name[0] ?? ''}${
                                      selectedAssignee[0]?.last_name[0] ?? ''
                                    }`
                                  : ''}
                              </StringAvatarBox>
                            </InputAdornment>
                          ),
                        }}
                      />
                    );
                  }}
                  value={selectedAssignee[0] ?? null}
                  onChange={handleChangeAssignee}
                  onInputChange={handleSearchInputChangeAssignee}
                />
              </StyledAssignee>
            )}
            {/* Only show team component if user has either show or update permission */}
            {(hasShowTeamPermission || hasUpdateTeamPermission) && (
              <StyledTeam>
                <StyledTeamLabel>
                  Team:
                  <Tooltip title={t('team.tooltip')} placement="top">
                    <FontAwesomeIcon icon={faInfoCircle} />
                  </Tooltip>
                </StyledTeamLabel>
                <Autocomplete
                  id="team-list"
                  loading={isTeamsLoading}
                  options={optionsTeam}
                  sx={{ width: 300, lg: { width: 450 } }}
                  getOptionLabel={(option: TTeam) => `${option.name}`}
                  noOptionsText={
                    isTeamsLoading ? t('team.loading') : t('team.noOptions')
                  }
                  popupIcon={<FontAwesomeIcon icon={faMagnifyingGlass} />}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  disabled={!hasUpdateTeamPermission}
                  renderOption={(props: any, option) => {
                    const { key, ...optionProps } = props;

                    return (
                      <div key={key} {...optionProps}>
                        <p>{option.name}</p>
                      </div>
                    );
                  }}
                  renderInput={(params) => {
                    return (
                      <TextField
                        {...params}
                        variant="outlined"
                        placeholder={t('team.unassigned') ?? ''}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: params.inputProps.value && (
                            <InputAdornment position="start">
                              {selectedTeam?.name ?? ''}
                            </InputAdornment>
                          ),
                        }}
                      />
                    );
                  }}
                  value={selectedTeam}
                  onChange={handleChangeTeam}
                  onInputChange={handleSearchInputChangeTeam}
                />
              </StyledTeam>
            )}
            {isInviteIdReady && hasListWatcherPermission && (
              <div className="watchers-row">
                <Watchers
                  inviteId={
                    store.contextInviteIdForNewInvite || params.id || ''
                  }
                />
              </div>
            )}
          </RightContent>
        )}
      </StyledHead>
    </div>
  );
};

export default InviteHead;
